import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { SharedModule } from 'app/shared/shared.module';

@Component({
  selector: 'mh-credentials-step',
  standalone: true,
  imports: [SharedModule, ReactiveFormsModule, RouterModule],
  templateUrl: './credentials-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'tw-block' },
})
export class CredentialsStepComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly initialEmail = input('');
  readonly passwordRequired = input(false);
  readonly loading = input(false);
  readonly errorKey = input<string | null>(null);
  readonly errorParams = input<Record<string, string | number>>({});

  readonly submitted = output<{ email: string; password: string }>();
  readonly errorDismissed = output<void>();
  readonly backRequested = output<void>();

  readonly email = new FormControl('', [Validators.required, Validators.email]);
  readonly password = new FormControl('');
  readonly form = new FormGroup({ email: this.email, password: this.password });

  readonly showPassword = signal(false);

  private readonly formStatus = signal(this.form.status);
  readonly canSubmit = computed(() => this.formStatus() === 'VALID' && !this.loading());

  constructor() {
    this.form.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status) => this.formStatus.set(status));

    effect(() => {
      const seed = this.initialEmail();
      if (!seed || this.email.value) return;

      this.email.setValue(seed, { emitEvent: false });
      this.formStatus.set(this.form.status);
    });

    effect(() => {
      const validators = this.passwordRequired() ? [Validators.required] : [];
      this.password.setValidators(validators);
      this.password.updateValueAndValidity({ emitEvent: false });
      this.formStatus.set(this.form.status);
    });

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.errorDismissed.emit());

    this.email.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.passwordRequired()) this.back();
    });
  }

  submit(): void {
    if (!this.canSubmit()) return;

    this.submitted.emit({ email: this.email.value.trim(), password: this.password.value ?? '' });
  }

  back(): void {
    this.password.setValue('', { emitEvent: false });
    this.showPassword.set(false);
    this.backRequested.emit();
  }
}
